"use client"

import type {ReactNode} from "react"
import {FileText, Folder, GitBranch, MessageCircle, MessageSquare, StickyNote, Ticket, Upload, Zap} from "lucide-react"
import {cn} from "@/lib/utils"
import {
  IconBitBucket,
  IconGitLab,
  IconGithub,
  IconGoogleDrive,
  IconGranola,
  IconJira,
  IconLinear,
  IconSlack,
  IconTeams,
  IconZoom,
} from "@/app/components/icons/Icons"
import {
  evidenceProviderFromSourceType,
  type EvidenceProvider,
} from "@/app/components/evidence/evidenceProvider"
import type {SignalSourceKind} from "@/app/components/signals/signalKinds"

const PROVIDER_TILE_CLASSES: Record<EvidenceProvider, string> = {
  slack: "bg-[#f4f0ff]",
  teams: "bg-[#eef0ff]",
  zoom: "bg-[#e6f1ff]",
  jira: "bg-[#e8f0ff]",
  linear: "bg-[#eeedff]",
  ticket: "bg-amber-50",
  change: "bg-emerald-100 text-emerald-700",
  github: "bg-zinc-100",
  bitbucket: "bg-blue-50",
  gitlab: "bg-orange-50",
  signal: "bg-purple-100 text-purple-600",
  insight: "bg-violet-100 text-violet-600",
  discussion: "bg-purple-100 text-purple-600",
  notion: "bg-zinc-100",
  confluence: "bg-blue-50",
  google_drive: "bg-emerald-50",
  upload: "bg-slate-50",
  granola: "bg-amber-50",
  otter: "bg-sky-50",
  call_note: "bg-rose-50",
  text_note: "bg-amber-50 text-amber-700",
  feature: "bg-sky-50",
  project: "bg-slate-50",
  document: "bg-slate-50",
  unknown: "bg-muted",
}

function renderProviderIcon(provider: EvidenceProvider, className: string): ReactNode {
  switch (provider) {
    case "slack":
      return <IconSlack className={className}/>
    case "teams":
      return <IconTeams className={className}/>
    case "zoom":
      return <IconZoom className={className}/>
    case "jira":
      return <IconJira className={className}/>
    case "linear":
      return <IconLinear className={className}/>
    case "change":
      return <GitBranch className={className} aria-hidden/>
    case "github":
      return <IconGithub className={className}/>
    case "bitbucket":
      return <IconBitBucket className={className}/>
    case "gitlab":
      return <IconGitLab className={className}/>
    case "signal":
    case "discussion":
      return <MessageCircle className={className} aria-hidden/>
    case "insight":
      return <InsightTargetIcon className={className}/>
    case "google_drive":
      return <IconGoogleDrive className={className}/>
    case "granola":
      return <IconGranola className={className}/>
    case "notion":
      return <NotionMark className={className}/>
    case "confluence":
      return <ConfluenceMark className={className}/>
    case "upload":
      return <Upload className={className} aria-hidden/>
    case "otter":
    case "call_note":
      return <MessageSquare className={className} aria-hidden/>
    case "text_note":
      return <StickyNote className={className} aria-hidden/>
    case "feature":
      return <Zap className={className} aria-hidden/>
    case "project":
      return <Folder className={className} aria-hidden/>
    case "ticket":
      return <Ticket className={className} aria-hidden/>
    case "document":
      return <FileText className={className} aria-hidden/>
    default:
      return <FileText className={className} aria-hidden/>
  }
}

function InsightTargetIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  )
}

function NotionMark({className}: {className?: string}) {
  return (
    <span className={cn(
      "inline-flex items-center justify-center rounded bg-foreground text-[10px] font-bold leading-none text-background",
      className,
    )}>
      N
    </span>
  )
}

function ConfluenceMark({className}: {className?: string}) {
  return (
    <span className={cn(
      "inline-flex items-center justify-center rounded bg-blue-600 text-[10px] font-bold leading-none text-white",
      className,
    )}>
      C
    </span>
  )
}

/** Raw provider mark without the tinted tile — used inside evidence pills. */
export function EvidenceProviderGlyph({
  provider,
  className = "size-3.5",
}: {
  provider: EvidenceProvider
  className?: string
}) {
  return <>{renderProviderIcon(provider, cn(className, provider === "granola" && "dark:invert"))}</>
}

export function EvidenceProviderIcon({
  provider,
  sourceType,
  size = "md",
}: {
  provider?: EvidenceProvider
  sourceType?: SignalSourceKind | string
  size?: "xxs" | "xs" | "sm" | "md"
}) {
  const resolved = provider ?? (sourceType ? evidenceProviderFromSourceType(sourceType) : "unknown")
  const tileClass = size === "xxs"
    ? "size-3 rounded-full"
    : size === "xs"
    ? "size-5 rounded-full"
    : size === "sm"
      ? "size-7"
      : "size-9"
  const iconClass = size === "xxs" ? "size-2" : size === "xs" ? "size-2.5" : size === "sm" ? "size-3.5" : "size-4"

  return (
    <div className={cn(
      "flex shrink-0 items-center justify-center rounded-lg",
      tileClass,
      PROVIDER_TILE_CLASSES[resolved],
    )}>
      {renderProviderIcon(resolved, iconClass)}
    </div>
  )
}
